import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, HandHeart, Banknote, Package, Wallet, Info, Smartphone, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription } from "../ui/alert";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { basarGiftUrl, nok } from "../../lib/basar";
import { normalizePhone } from "./BuyPanel";
import { useLang } from "../../context/LanguageContext";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const QUICK_AMOUNTS = [100, 250, 500, 1000];

/**
 * «Gi premie» (brukarval 2026-10-30): privatpersonar og bedrifter kan støtte basaren med ei pengegåve (Vipps, direkte til arrangøren)
 * eller ei tinggåve (namn + obligatorisk verdi, ev. fleire like eksemplar). Gåva blir premie automatisk; givaren er aldri offentleg.
 * `basar` = offentleg basarvisning (can_gift, can_gift_money, gift_min_amount).
 */
export const GiveGiftDialog = ({ basar, trigger, className = "" }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const minAmount = Math.max(1, Number(basar.gift_min_amount || 100));
  const moneyOk = !!basar.can_gift_money;
  const [kind, setKind] = useState(moneyOk ? "money" : "item");
  const [form, setForm] = useState({ amount: "", name: "", description: "", value: "", count: "1", donor_name: "", donor_kind: "private", donor_phone: "", donor_email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setKind(moneyOk ? "money" : "item");
  }, [open, moneyOk]);

  const amount = parseInt(form.amount, 10) || 0;
  const value = parseInt(form.value, 10) || 0;
  const count = Math.max(1, parseInt(form.count, 10) || 1);
  const detailsValid = form.donor_name.trim().length >= 2 && !!normalizePhone(form.donor_phone) && (!form.donor_email.trim() || EMAIL_RE.test(form.donor_email.trim()));
  const giftValid = kind === "money" ? amount >= minAmount : form.name.trim().length >= 2 && value >= 1;
  const canSubmit = basar.can_gift && giftValid && detailsValid && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (kind === "money" && amount < minAmount) {
      toast.error(t("Minste pengegave er {amount}", { amount: nok(minAmount) }));
      return;
    }
    if (kind === "item" && form.name.trim().length < 2) {
      toast.error(t("Skriv hva gaven er"));
      return;
    }
    if (kind === "item" && value < 1) {
      toast.error(t("Oppgi verdien på gaven i kroner"));
      return;
    }
    if (form.donor_name.trim().length < 2) {
      toast.error(t("Fyll inn navnet ditt"));
      return;
    }
    const phone = normalizePhone(form.donor_phone);
    if (!phone) {
      toast.error(t("Fyll inn et gyldig mobilnummer (8 siffer)"));
      return;
    }
    const email = form.donor_email.trim();
    if (email && !EMAIL_RE.test(email)) {
      toast.error(t("E-postadressen ser ikke riktig ut"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        kind, name: kind === "item" ? form.name.trim() : "", description: kind === "item" ? form.description.trim() : "", value: kind === "money" ? amount : value, count: kind === "item" ? count : 1,
        donor_name: form.donor_name.trim(), donor_kind: form.donor_kind, donor_phone: phone, donor_email: email, message: form.message.trim(),
      };
      const { data } = await api.post(`/basar/${basar.id}/gifts`, payload);
      if (data.payment_redirect_url) {
        window.location.assign(data.payment_redirect_url);
        return;
      }
      setOpen(false);
      toast.success(t("Tusen takk! Gaven er registrert som premie."));
      navigate(basarGiftUrl(data.gift.id));
    } catch (err) {
      toast.error(errorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className={`border-[hsl(var(--warning))]/50 text-foreground hover:bg-[hsl(var(--warning))]/10 ${className}`} data-testid="basar-give-gift-button">
            <HandHeart className="w-4 h-4 mr-1.5 text-[hsl(var(--warning))]" /> {t("Gi premie")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="basar-gift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandHeart className="w-5 h-5 text-[hsl(var(--warning))]" /> {t("Gi en premie til {basar}", { basar: basar.name })}</DialogTitle>
          <DialogDescription>{t("Privatpersoner og bedrifter kan støtte basaren med en pengegave eller en gjenstand. Gaven blir med i premielisten med en gang – navnet ditt vises ikke offentlig.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {/* Type gåve */}
          <RadioGroup value={kind} onValueChange={setKind} className="grid grid-cols-2 gap-2" data-testid="basar-gift-kind-group">
            <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${kind === "money" ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"} ${!moneyOk ? "opacity-60 cursor-not-allowed" : ""}`}>
              <RadioGroupItem value="money" id="gift-kind-money" disabled={!moneyOk} className="mt-0.5" data-testid="basar-gift-kind-money" />
              <span className="text-sm min-w-0">
                <span className="flex items-center gap-1.5 font-medium"><Banknote className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Pengegave")}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{moneyOk ? t("Betales med Vipps og blir en pengepremie") : t("Arrangøren har ikke Vipps ennå")}</span>
              </span>
            </label>
            <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${kind === "item" ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"}`}>
              <RadioGroupItem value="item" id="gift-kind-item" className="mt-0.5" data-testid="basar-gift-kind-item" />
              <span className="text-sm min-w-0">
                <span className="flex items-center gap-1.5 font-medium"><Package className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Tinggave")}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{t("En gjenstand, et gavekort e.l. – arrangøren henter den")}</span>
              </span>
            </label>
          </RadioGroup>

          {kind === "money" ? (
            <div className="space-y-3" data-testid="basar-gift-money-section">
              <div className="space-y-1.5">
                <Label htmlFor="gift-amount">{t("Beløp (kr)")}</Label>
                <Input id="gift-amount" type="number" inputMode="numeric" min={minAmount} step={1} value={form.amount} onChange={(e) => set("amount", e.target.value.replace(/\D/g, "").slice(0, 7))} placeholder={String(minAmount)} required className="text-lg font-heading font-semibold tabular-nums h-11" data-testid="basar-gift-amount-input" />
                <p className="text-xs text-muted-foreground">{t("Minste beløp er {amount}. Beløpet blir en egen pengepremie i basaren.", { amount: nok(minAmount) })}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {QUICK_AMOUNTS.filter((a) => a >= minAmount).map((a) => (
                  <Button key={a} type="button" size="sm" variant={amount === a ? "default" : "secondary"} onClick={() => set("amount", String(a))} data-testid={`basar-gift-quick-${a}`}>{nok(a)}</Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="basar-gift-item-section">
              <div className="space-y-1.5">
                <Label htmlFor="gift-name">{t("Hva er gaven?")}</Label>
                <Input id="gift-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} placeholder={t("F.eks. Rosebukett fra Spar Sæbøvik")} required data-testid="basar-gift-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="gift-value">{t("Verdi (kr)")}</Label>
                  <Input id="gift-value" type="number" inputMode="numeric" min={1} value={form.value} onChange={(e) => set("value", e.target.value.replace(/\D/g, "").slice(0, 7))} placeholder="350" required data-testid="basar-gift-value-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gift-count">{t("Antall like")}</Label>
                  <Input id="gift-count" type="number" inputMode="numeric" min={1} max={100} value={form.count} onChange={(e) => set("count", e.target.value.replace(/\D/g, "").slice(0, 3))} data-testid="basar-gift-count-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("Verdi per stykk må oppgis. Gir du flere like, trekkes det like mange vinnere.")}</p>
              <div className="space-y-1.5">
                <Label htmlFor="gift-desc">{t("Beskrivelse (valgfritt)")}</Label>
                <Textarea id="gift-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} maxLength={600} placeholder={t("Størrelse, farge, innhold …")} data-testid="basar-gift-description-input" />
              </div>
            </div>
          )}

          {/* Givar */}
          <div className="space-y-3 border-t border-border pt-4" data-testid="basar-gift-donor-section">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">{t("Om deg")}</Label>
              <RadioGroup value={form.donor_kind} onValueChange={(v) => set("donor_kind", v)} className="flex items-center gap-3" data-testid="basar-gift-donor-kind-group">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="private" id="donor-private" data-testid="basar-gift-donor-private" /> {t("Privatperson")}</label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="company" id="donor-company" data-testid="basar-gift-donor-company" /> {t("Bedrift")}</label>
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gift-donor-name">{form.donor_kind === "company" ? t("Bedriftens navn") : t("Navn")}</Label>
              <Input id="gift-donor-name" value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)} autoComplete={form.donor_kind === "company" ? "organization" : "name"} maxLength={120} required data-testid="basar-gift-donor-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="gift-donor-phone">{t("Mobilnummer")}</Label>
                <div className="relative">
                  <Smartphone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="gift-donor-phone" type="tel" inputMode="tel" value={form.donor_phone} onChange={(e) => set("donor_phone", e.target.value)} autoComplete="tel" maxLength={20} placeholder="912 34 567" className="pl-9" required data-testid="basar-gift-donor-phone-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gift-donor-email">{t("E-post (valgfritt)")}</Label>
                <Input id="gift-donor-email" type="email" value={form.donor_email} onChange={(e) => set("donor_email", e.target.value)} autoComplete="email" maxLength={200} data-testid="basar-gift-donor-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gift-message">{t("Melding til arrangøren (valgfritt)")}</Label>
              <Textarea id="gift-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} maxLength={600} placeholder={t("F.eks. når gaven kan hentes")} data-testid="basar-gift-message-input" />
            </div>
            <p className="text-xs text-muted-foreground inline-flex items-start gap-1.5"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t("Kontaktopplysningene brukes bare av arrangøren til å avtale henting og takke. Oppgir du e-post, får du en bekreftelse.")}</p>
          </div>

          <Button type="submit" className="w-full h-11 text-base" disabled={!canSubmit} data-testid="basar-gift-submit-button">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : kind === "money" ? <Wallet className="w-4 h-4 mr-2" /> : <HandHeart className="w-4 h-4 mr-2" />}
            {submitting ? (kind === "money" ? t("Sender deg til Vipps …") : t("Registrerer …")) : kind === "money" ? (amount >= minAmount ? t("Betal med Vipps – {amount}", { amount: nok(amount) }) : t("Betal med Vipps")) : t("Gi gaven")}
          </Button>
          {kind === "money" && (
            <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-gift-money-note">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">{t("Pengegaven går direkte til {org} sin Vipps-konto og blir lagt inn som premie når betalingen er godkjent. Right in formidler bare gaven.", { org: basar.organizer_name })}</AlertDescription>
            </Alert>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
